import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';
import { useOptionalAuth } from '../contexts/AuthContext';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';
import type { ProjectMemberRow } from './ProjectMembersPanel';

// #2846 — the ONE reassign-owner control for all three registers (Open
// Question, Risk, NFR), mirroring the backend's one shared
// ownershipReassignmentService.ts rather than three per-page copies.
//
// Candidate population: every project_members row on the project, Delivery
// or Business tier, de-duplicated by user — exactly
// assertValidReassignTarget's rule (existence-only, no role allowlist).
// GET /projects/:id/members returns that same unfiltered set, so the picker
// never offers a target the server would 422, and never hides one it would
// accept.
//
// Visibility: the Reassign action is shown only to callers who could
// plausibly pass assertReassignAuthority — the project's PRIMARY
// PRODUCT_MANAGER, or a TENANT_ADMIN/ADMIN. This is a UI courtesy, not the
// gate: the server check is authoritative, and a 403 that still gets
// through (e.g. an admin whose current tenant isn't the project's) is
// surfaced as a message, not swallowed.

export type OwnableEntityType = 'open_question' | 'risk' | 'nfr';

const OWNER_ROUTE_SEGMENT: Record<OwnableEntityType, string> = {
  open_question: 'open-questions',
  risk: 'risks',
  nfr: 'nfrs',
};

export interface OwnerCandidate {
  userId: string;
  userName: string;
  roles: string[];
}

export interface ProjectOwnership {
  candidates: OwnerCandidate[];
  canReassign: boolean;
  /** False until the member list has loaded (or failed to). */
  loaded: boolean;
  /**
   * True only when the member list actually loaded. False for a caller the
   * members route refuses — notably an active project_stakeholders viewer of
   * OpenQuestionsPage, whose list route (requireProjectMemberOrActiveStakeholder)
   * is wider than GET /members (requireProjectMember). Without the list no
   * owner can be named truthfully, so the control renders nothing rather
   * than mislabel a current member as "Former project member".
   */
  available: boolean;
}

function roleLabel(role: string): string {
  return role
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Fetches the project's members once per page (not once per row) and derives
 * both the candidate list and whether the current user may reassign. A failed
 * fetch degrades to "unavailable": no owner line, no Reassign action.
 */
export function useProjectOwnership(projectId: string | undefined): ProjectOwnership {
  const auth = useOptionalAuth();
  const { myTenant } = useMyTenant();
  const [members, setMembers] = useState<ProjectMemberRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    apiFetch<ProjectMemberRow[]>(`/projects/${projectId}/members`)
      .then((rows) => {
        if (cancelled) return;
        setMembers(Array.isArray(rows) ? rows : []);
        setAvailable(true);
      })
      .catch(() => { if (!cancelled) { setMembers([]); setAvailable(false); } })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [projectId]);

  return useMemo(() => {
    const byUser = new Map<string, OwnerCandidate>();
    for (const m of members) {
      const existing = byUser.get(m.userId);
      if (existing) {
        if (!existing.roles.includes(m.role)) existing.roles.push(m.role);
      } else {
        byUser.set(m.userId, { userId: m.userId, userName: m.userName, roles: [m.role] });
      }
    }
    const candidates = [...byUser.values()].sort((a, b) => a.userName.localeCompare(b.userName));

    const userId = auth?.user?.id;
    const isPrimaryPm = !!userId && members.some(
      (m) => m.userId === userId && m.role === 'PRODUCT_MANAGER' && m.memberType === 'PRIMARY',
    );
    const canReassign = !!userId && (isPrimaryPm || isAdminRole(myTenant?.role));

    return { candidates, canReassign, loaded, available };
  }, [members, loaded, available, auth?.user?.id, myTenant?.role]);
}

function describeReassignError(e: unknown): string {
  const status = (e as { status?: unknown } | null)?.status;
  if (status === 403) return 'Only the project PM or a tenant admin can reassign ownership.';
  if (status === 422) return 'That person is no longer a member of this project. Refresh and choose someone else.';
  if (status === 404) return 'This item no longer exists. Refresh the page.';
  return e instanceof Error && e.message ? e.message : 'Reassignment failed.';
}

interface OwnerReassignControlProps {
  projectId: string;
  entityType: OwnableEntityType;
  entityId: string;
  ownerId: string | null;
  ownership: ProjectOwnership;
  onReassigned: (newOwnerUserId: string) => void;
}

export default function OwnerReassignControl({
  projectId, entityType, entityId, ownerId, ownership, onReassigned,
}: OwnerReassignControlProps) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (ownership.loaded && !ownership.available) return null;

  const owner = ownerId ? ownership.candidates.find((c) => c.userId === ownerId) : undefined;
  // An ownerId with no current project_members row (e.g. the PM was removed
  // after default assignment — #2746) is shown as such, not as "Unassigned".
  const ownerText = !ownerId
    ? 'Unassigned'
    : owner
      ? owner.userName
      : ownership.loaded ? 'Former project member' : '…';
  const options = ownership.candidates.filter((c) => c.userId !== ownerId);

  const startEditing = () => {
    setSelected('');
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/projects/${projectId}/${OWNER_ROUTE_SEGMENT[entityType]}/${entityId}/owner`, {
        method: 'PATCH',
        body: JSON.stringify({ newOwnerUserId: selected }),
      });
      setEditing(false);
      onReassigned(selected);
    } catch (e) {
      setError(describeReassignError(e));
    } finally {
      setSaving(false);
    }
  };

  const textStyle = { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' };
  const buttonStyle = {
    fontFamily: 'var(--font-body)', fontSize: 12, padding: '2px 8px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
  };

  return (
    <div data-testid={`owner-control-${entityId}`} style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={textStyle}>
          Owner: <span data-testid={`owner-name-${entityId}`} style={{ color: 'var(--text)' }}>{ownerText}</span>
        </span>
        {ownership.canReassign && !editing && (
          <button type="button" style={buttonStyle} onClick={startEditing}>Reassign</button>
        )}
      </div>

      {ownership.canReassign && editing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <select
            aria-label="New owner"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={saving}
            style={{ ...buttonStyle, cursor: 'default', minWidth: 200 }}
          >
            <option value="">Choose a project member…</option>
            {options.map((c) => (
              <option key={c.userId} value={c.userId}>
                {c.userName} · {c.roles.map(roleLabel).join(', ')}
              </option>
            ))}
          </select>
          <button type="button" style={buttonStyle} onClick={() => void save()} disabled={!selected || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" style={buttonStyle} onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </button>
        </div>
      )}

      {error && (
        <p role="alert" style={{ ...textStyle, color: 'var(--error, #ef4444)', margin: '6px 0 0' }}>{error}</p>
      )}
    </div>
  );
}
